/**
 * KittenPen — collection zone for calmed kittens.
 *
 * A fenced area. Calmed kittens that enter the zone are "penned"
 * (safe). When all required kittens are penned, the puzzle is solved.
 *
 * The pen has a sprung gate (a brass bar grille across its front). The
 * Kitten Corral puzzle opens it only while Basil is anchored on the gate
 * plate (`setGateOpen`); shut, it is copper and down, open it slides up
 * and glows emerald. `tickGate` eases it between the two.
 *
 * Visually: fence posts and rails made from copper-colored cylinders.
 * A counter label shows progress (e.g. "1/3").
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { tagAs } from "../../levels/PlaceholderTag";
import { KittenShelterVisuals } from "../../levels/art/KittenShelterVisuals";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";

const SAFE_GLOW_Z = 0.18;
const SAFE_MARKER_Z = 0.28;
const SAFE_MARKER_DIAMETER = 0.32;
const SAFE_MARKER_DIM_ALPHA = 0.34;
const SAFE_MARKER_LIT_ALPHA = 0.95;
const SAFE_GLOW_MIN_ALPHA = 0.06;
const SAFE_GLOW_MAX_ALPHA = 0.26;
/** Gate grille: in front of the fence, bars slide up this far when open. */
const GATE_Z = -0.14;
const GATE_BARS = 7;
const GATE_OPEN_LIFT = 1.6;
/** Seconds for the gate to swing fully open or shut. */
const GATE_TRAVEL_SECONDS = 0.35;

export interface KittenPenConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  requiredCount?: number;
  physicalShelter?: boolean;
  shadowGenerator?: ShadowGenerator;
}

export class KittenPen extends InteractiveObject {
  private pennedCount = 0;
  private readonly requiredCount: number;

  private fenceParts: Mesh[] = [];
  private counterPlane: Mesh;
  private counterText: TextBlock;
  private safeGlowMat: StandardMaterial | null = null;
  private safeMarkerMats: StandardMaterial[] = [];
  private gateRoot: TransformNode | null = null;
  private gateMat: StandardMaterial | null = null;
  private gateOpen = false;
  /** 0 = shut, 1 = fully open (eased by tickGate). */
  private gateLift = 0;
  private shelter: KittenShelterVisuals | null = null;

  /** Detection radius for kittens entering the pen. */
  public readonly penRadius = 3.1;

  constructor(scene: Scene, config: KittenPenConfig) {
    const size = config.size ?? new Vector3(4.8, 2.4, 1.0);
    super(scene, { ...config, size });

    this.requiredCount = config.requiredCount ?? 3;

    const copperMat = new StandardMaterial(`penFence_${config.key}_mat`, scene);
    copperMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    copperMat.specularColor = new Color3(0.2, 0.15, 0.05);
    copperMat.emissiveColor = Color3.Black();

    const hw = size.x / 2;
    const hh = size.y / 2;

    // Fence posts (vertical cylinders at corners + midpoints)
    const postXPositions = [-hw, -hw / 2, 0, hw / 2, hw];
    for (let i = 0; i < postXPositions.length; i++) {
      // Left side
      const post = MeshBuilder.CreateCylinder(
        `pen_post_${config.key}_${i}`,
        { diameter: 0.1, height: size.y, tessellation: 8 },
        scene,
      );
      post.parent = this.rootMesh;
      post.position = new Vector3(postXPositions[i], 0, 0);
      post.material = copperMat;
      this.fenceParts.push(post);
    }

    // Horizontal rails (top and middle)
    for (const railY of [hh - 0.1, 0, -hh + 0.1]) {
      const rail = MeshBuilder.CreateCylinder(
        `pen_rail_${config.key}_${railY}`,
        { diameter: 0.05, height: size.x, tessellation: 8 },
        scene,
      );
      rail.parent = this.rootMesh;
      rail.rotation.z = Math.PI / 2;
      rail.position = new Vector3(0, railY, 0);
      rail.material = copperMat;
      this.fenceParts.push(rail);
    }

    // Counter display (3D text plane)
    this.counterPlane = MeshBuilder.CreatePlane(
      `penCounter_${config.key}`,
      { width: 1.2, height: 0.5 },
      scene,
    );
    this.counterPlane.parent = this.rootMesh;
    this.counterPlane.position = new Vector3(0, hh + 0.5, 0);
    this.counterPlane.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const advancedTexture = AdvancedDynamicTexture.CreateForMesh(
      this.counterPlane,
      256,
      128,
    );

    this.counterText = new TextBlock("penCounterText");
    this.counterText.text = `0/${this.requiredCount}`;
    this.counterText.color = COLORS.BRASS;
    this.counterText.fontSize = 48;
    this.counterText.fontFamily = "monospace";
    advancedTexture.addControl(this.counterText);

    this.createSafeFeedback(config.key, size);
    this.createGate(config.key, size);
    this.material = copperMat;
    if (config.physicalShelter) {
      const fallback = this.rootMesh.getChildMeshes();
      this.shelter = new KittenShelterVisuals(scene,
        new Vector3(config.position.x, config.position.y - size.y / 2, config.position.z),
        () => { for (const mesh of fallback) mesh.isVisible = false; }, config.shadowGenerator);
    }
  }

  /** Open or shut the pen gate (eased over GATE_TRAVEL_SECONDS by tickGate). */
  setGateOpen(open: boolean): void {
    this.gateOpen = open;
  }

  get isGateOpen(): boolean {
    return this.gateOpen;
  }

  /** Advance the gate animation toward its open/shut target. */
  tickGate(dt: number): void {
    const target = this.gateOpen ? 1 : 0;
    if (this.gateLift === target) return;
    const step = dt / GATE_TRAVEL_SECONDS;
    this.gateLift = target > this.gateLift
      ? Math.min(target, this.gateLift + step)
      : Math.max(target, this.gateLift - step);
    this.applyGateVisual();
  }

  /** Record a kitten entering the pen. Returns true if all kittens penned. */
  penKitten(): boolean {
    this.pennedCount++;
    this.counterText.text = `${this.pennedCount}/${this.requiredCount}`;
    this.updateSafeFeedback();

    gameEvents.emit(GameEvents.KITTEN_PENNED, {
      key: this.key,
      count: this.pennedCount,
      required: this.requiredCount,
    });

    if (this.pennedCount >= this.requiredCount) {
      this.solve();
      this.counterText.color = COLORS.EMERALD;
      return true;
    }

    this.state = "active";
    this.updateMaterialForState();
    return false;
  }

  get isComplete(): boolean {
    return this.pennedCount >= this.requiredCount;
  }

  /** Check if a world position is within the pen area. */
  isInsidePen(position: Vector3): boolean {
    const dx = this.rootMesh.position.x - position.x;
    const dz = this.rootMesh.position.z - position.z;
    return Math.sqrt(dx * dx + dz * dz) < this.penRadius;
  }

  override dispose(): void {
    this.shelter?.dispose(); this.shelter = null;
    super.dispose();
    this.gateRoot?.dispose();
    this.gateMat?.dispose();
    this.safeGlowMat?.dispose();
    for (const mat of this.safeMarkerMats) {
      mat.dispose();
    }
    this.safeGlowMat = null;
    this.safeMarkerMats = [];
  }

  private createSafeFeedback(key: string, size: Vector3): void {
    const glow = MeshBuilder.CreatePlane(
      `penSafeGlow_${key}`,
      { width: size.x + 0.55, height: size.y + 0.4 },
      this.scene,
    );
    glow.parent = this.rootMesh;
    glow.position = new Vector3(0, 0, SAFE_GLOW_Z);
    glow.isPickable = false;

    this.safeGlowMat = new StandardMaterial(`penSafeGlow_${key}_mat`, this.scene);
    this.safeGlowMat.diffuseColor = Color3.FromHexString(COLORS.EMERALD);
    this.safeGlowMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.35);
    this.safeGlowMat.alpha = 0;
    this.safeGlowMat.specularColor = Color3.Black();
    this.safeGlowMat.disableLighting = true;
    this.safeGlowMat.metadata = { preserveAffordanceEmissive: true };
    glow.material = this.safeGlowMat;
    tagAs(glow, "modular-final");

    const slotSpan = size.x * 0.62;
    const markerY = size.y / 2 + 0.28;
    for (let i = 0; i < this.requiredCount; i++) {
      const marker = MeshBuilder.CreateSphere(
        `penSafeMarker_${key}_${i}`,
        { diameter: SAFE_MARKER_DIAMETER, segments: 12 },
        this.scene,
      );
      const t = this.requiredCount <= 1 ? 0.5 : i / (this.requiredCount - 1);
      marker.parent = this.rootMesh;
      marker.position = new Vector3((t - 0.5) * slotSpan, markerY, SAFE_MARKER_Z);
      marker.isPickable = false;

      const mat = new StandardMaterial(`penSafeMarker_${key}_${i}_mat`, this.scene);
      mat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
      mat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.08);
      mat.alpha = SAFE_MARKER_DIM_ALPHA;
      mat.specularColor = Color3.Black();
      mat.disableLighting = true;
      mat.metadata = { preserveAffordanceEmissive: true };
      marker.material = mat;
      tagAs(marker, "modular-final");
      this.safeMarkerMats.push(mat);
    }
  }

  private createGate(key: string, size: Vector3): void {
    const root = new TransformNode(`penGate_${key}`, this.scene);
    root.parent = this.rootMesh;
    root.position = new Vector3(0, 0, GATE_Z);
    this.gateRoot = root;

    const mat = new StandardMaterial(`penGate_${key}_mat`, this.scene);
    mat.specularColor = Color3.Black();
    mat.disableLighting = true;
    mat.metadata = { preserveAffordanceEmissive: true };
    this.gateMat = mat;

    const w = size.x * 0.96;
    const h = size.y * 0.9;
    for (let i = 0; i < GATE_BARS; i++) {
      const bar = MeshBuilder.CreateBox(
        `penGate_${key}_bar_${i}`,
        { width: 0.07, height: h, depth: 0.05 },
        this.scene,
      );
      bar.parent = root;
      bar.position = new Vector3(-w / 2 + (w * i) / (GATE_BARS - 1), 0, 0);
      bar.material = mat;
      bar.isPickable = false;
      tagAs(bar, "modular-final");
    }
    for (const y of [h / 2, -h / 2]) {
      const rail = MeshBuilder.CreateBox(
        `penGate_${key}_rail_${y > 0 ? "top" : "bottom"}`,
        { width: w + 0.1, height: 0.09, depth: 0.06 },
        this.scene,
      );
      rail.parent = root;
      rail.position = new Vector3(0, y, 0);
      rail.material = mat;
      rail.isPickable = false;
      tagAs(rail, "modular-final");
    }
    this.applyGateVisual();
  }

  private applyGateVisual(): void {
    this.shelter?.setState(this.gateLift, this.pennedCount);
    if (!this.gateRoot || !this.gateMat) return;
    const t = this.gateLift;
    this.gateRoot.position.y = t * GATE_OPEN_LIFT;
    const copper = Color3.FromHexString(COLORS.COPPER);
    const emerald = Color3.FromHexString(COLORS.EMERALD);
    const c = Color3.Lerp(copper, emerald, t);
    this.gateMat.diffuseColor = c;
    this.gateMat.emissiveColor = c.scale(0.35 + 0.35 * t);
    this.gateMat.alpha = 0.95 - 0.55 * t;
  }

  private updateSafeFeedback(): void {
    this.shelter?.setState(this.gateLift, this.pennedCount);
    const count = Math.min(this.pennedCount, this.requiredCount);
    const progress = this.requiredCount <= 0 ? 1 : count / this.requiredCount;
    const emerald = Color3.FromHexString(COLORS.EMERALD);
    const brass = Color3.FromHexString(COLORS.BRASS);

    if (this.safeGlowMat) {
      this.safeGlowMat.alpha =
        progress <= 0
          ? 0
          : SAFE_GLOW_MIN_ALPHA + (SAFE_GLOW_MAX_ALPHA - SAFE_GLOW_MIN_ALPHA) * progress;
      this.safeGlowMat.emissiveColor = emerald.scale(0.28 + progress * 0.42);
    }

    for (let i = 0; i < this.safeMarkerMats.length; i++) {
      const mat = this.safeMarkerMats[i];
      if (i < count) {
        mat.diffuseColor = emerald;
        mat.emissiveColor = emerald.scale(0.75);
        mat.alpha = SAFE_MARKER_LIT_ALPHA;
      } else {
        mat.diffuseColor = brass;
        mat.emissiveColor = brass.scale(0.08);
        mat.alpha = SAFE_MARKER_DIM_ALPHA;
      }
    }
  }
}
